import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../../auth/AuthContext";
import { AUTOCOMPLETE_BLOQUEADO } from "../../utils/autofillNavegador";
import {
  Archive,
  ArchiveRestore,
  Camera,
  Check,
  CheckCheck,
  Download,
  Info,
  LayoutGrid,
  LifeBuoy,
  MessageSquare,
  Mic,
  Paperclip,
  Phone,
  Search,
  Send,
  Smile,
  Trash2,
  UserRoundPlus,
  Users,
  Video,
  X,
} from "lucide-react";
import {
  abrirConversaDiretaChat,
  arquivarConversaChat,
  criarGrupoChat,
  editarGrupoChat,
  excluirGrupoChat,
  enviarMensagemChat,
  listarColegasChat,
  listarConversasChat,
  listarMensagensChat,
  marcarConversaLidaChat,
  sinalizarDigitandoChat,
  type ColegaChat,
  type ConversaChat,
  type MensagemChat,
  type StatusColega,
} from "../../api/chatInterno";
import { useIdioma } from "../../i18n/IdiomaProvider";
import { urlAsset } from "../../utils/assets";
import { estiloAvatarCadastro } from "../tabela/AvatarCadastro";
import { mostrarToast, mostrarToastErro } from "../../utils/toast";
import { avisarNotificacao } from "../../utils/avisoNotificacao";
import { BotaoFechar } from "../ui/BotaoFechar";
import { CampoPesquisa } from "../ui/CampoPesquisa";
import { CampoTexto } from "../ui/CampoTexto";
import { Checkbox } from "../ui/Checkbox";
import { ChipSelecao } from "../ui/ChipSelecao";
import { ModalConfirmacao } from "../ui/ModalConfirmacao";
import { Popover } from "../ui/Popover";
import styles from "./ChatInterno.module.css";

interface Props {
  aberto: boolean;
  onFechar: () => void;
}

type FiltroChat = "todas" | "naoLidas" | "online" | "grupos" | "suporte";
type AbaChat = "conversas" | "equipe" | "arquivadas";

interface AnexoPendenteChat {
  arquivo: File;
  previewUrl: string;
}

type IconeChip = ComponentType<{ size?: string | number }>;

const FILTROS: Array<{ chave: FiltroChat; rotulo: string; Icone: IconeChip }> = [
  { chave: "todas", rotulo: "Todas", Icone: LayoutGrid },
  { chave: "naoLidas", rotulo: "Não lidas", Icone: PontoFiltro },
  { chave: "online", rotulo: "Online", Icone: PontoVerde },
  { chave: "suporte", rotulo: "Suporte", Icone: LifeBuoy },
];

const ABAS: Array<{ chave: AbaChat; rotulo: string; Icone: IconeChip }> = [
  { chave: "conversas", rotulo: "Conversas", Icone: MessageSquare },
  { chave: "equipe", rotulo: "Grupos", Icone: Users },
  { chave: "arquivadas", rotulo: "Arquivadas", Icone: Archive },
];

const EMOJIS = [
  "😀", "😅", "😍", "🤔", "👍", "👏", "🙏", "🎉",
  "🔥", "✅", "❌", "⚠️", "💡", "📌", "📎", "📊",
  "⏰", "🚀", "💰", "❤️",
];

/** Intervalo do polling enquanto o painel esta aberto (ms). */
const INTERVALO_POLL = 5_000;
/** Um sinal de "digitando" a cada N ms, no maximo. */
const INTERVALO_DIGITANDO = 3_000;
const TAMANHO_MAXIMO_ANEXO = 8 * 1024 * 1024;

function PontoFiltro({ size = 12 }: { size?: string | number }) {
  return (
    <svg viewBox="0 0 12 12" width={size} height={size} aria-hidden="true">
      <circle cx="6" cy="6" r="3.4" fill="currentColor" />
    </svg>
  );
}

function PontoVerde({ size = 12 }: { size?: string | number }) {
  return (
    <svg viewBox="0 0 12 12" width={size} height={size} aria-hidden="true">
      <circle cx="6" cy="6" r="3.4" fill="#22c55e" />
    </svg>
  );
}

function iniciais(texto: string): string {
  const limpo = String(texto ?? "").trim();
  if (!limpo) return "?";
  const partes = limpo.split(/\s+/);
  const primeira = partes[0]?.[0] ?? "";
  const segunda = partes.length > 1 ? partes[partes.length - 1][0] : "";
  return `${primeira}${segunda}`.toUpperCase() || "?";
}

function normalizar(texto: string): string {
  return texto
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

function AnexoMensagem({ id, nome }: { id: string; nome: string }) {
  const url = urlAsset(id);
  const nomeArquivo = nome || "Arquivo";
  const imagem = /\.(png|jpe?g|gif|webp)$/i.test(nomeArquivo);

  return (
    <div className={styles.balaoAnexoBloco}>
      {imagem && (
        <a href={url} target="_blank" rel="noreferrer" className={styles.balaoAnexoImagemLink}>
          <img className={styles.balaoAnexoImagem} src={url} alt={nomeArquivo} loading="lazy" />
        </a>
      )}
      <div className={styles.balaoAnexoAcoes}>
        <span className={`${styles.balaoAnexoNome} tooltip`} data-tooltip={nomeArquivo}>
          <Paperclip size={13} strokeWidth={1.9} aria-hidden="true" />
          <span data-i18n-db="true">{nomeArquivo}</span>
        </span>
        <a
          className={`${styles.balaoAnexoBaixar} tooltip`}
          href={url}
          download={nomeArquivo}
          aria-label={`Baixar ${nomeArquivo}`}
          data-tooltip="Baixar arquivo"
        >
          <Download size={15} strokeWidth={2} aria-hidden="true" />
          <span>Baixar</span>
        </a>
      </div>
    </div>
  );
}

function horaCurta(valor?: string | null): string {
  if (!valor) return "";
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return "";
  return data.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

/** Mantém contagens grandes compactas sem alterar o total armazenado. */
function totalNoBadge(total: number): string {
  return total > 99 ? "+99" : String(total);
}

function chaveDia(valor?: string | null): string {
  if (!valor) return "";
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return "";
  return `${data.getFullYear()}-${data.getMonth()}-${data.getDate()}`;
}

function rotuloDia(valor: string | null | undefined, traduzir: (t: string) => string): string {
  if (!valor) return "";
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return "";
  const hoje = new Date();
  const ontem = new Date();
  ontem.setDate(hoje.getDate() - 1);
  if (chaveDia(valor) === chaveDia(hoje.toISOString())) return traduzir("Hoje");
  if (chaveDia(valor) === chaveDia(ontem.toISOString())) return traduzir("Ontem");
  return data.toLocaleDateString();
}

function classeStatus(status: StatusColega | "" | undefined): string {
  switch (status) {
    case "DISPONIVEL":
      return styles.pontoDisponivel;
    case "AUSENTE":
      return styles.pontoAusente;
    case "OCUPADO":
      return styles.pontoOcupado;
    case "OFFLINE":
      return styles.pontoOffline;
    default:
      return "";
  }
}

function rotuloStatus(status: StatusColega | "" | undefined): string {
  switch (status) {
    case "DISPONIVEL": return "Disponível";
    case "AUSENTE": return "Ausente";
    case "OCUPADO": return "Ocupado";
    default: return "Offline";
  }
}

function Avatar({
  nome,
  avatarId,
  status,
  tamanho = 40,
  grupo = false,
}: {
  nome: string;
  avatarId?: string;
  status?: StatusColega | "";
  tamanho?: number;
  grupo?: boolean;
}) {
  const url = avatarId ? urlAsset(avatarId) : "";
  return (
    <span
      className={styles.avatar}
      data-grupo={grupo ? "true" : undefined}
      style={{
        ...(grupo ? {} : estiloAvatarCadastro(nome)),
        width: tamanho,
        height: tamanho,
        flexBasis: tamanho,
      }}
    >
      {url ? (
        <img src={url} alt="" />
      ) : grupo ? (
        <Users size={Math.round(tamanho * 0.45)} strokeWidth={1.9} />
      ) : (
        <span className={styles.avatarIniciais}>{iniciais(nome)}</span>
      )}
      {!grupo && (
        <span
          className={`${styles.avatarPonto} ${classeStatus(status || "OFFLINE")} tooltip`}
          data-tooltip={rotuloStatus(status)}
          aria-label={rotuloStatus(status)}
        />
      )}
    </span>
  );
}

export function ChatInterno({ aberto, onFechar }: Props) {
  const { usuario } = useAuth();
  const { traduzir } = useIdioma();
  const tipoUsuario = String(usuario?.tipoUsuario || "").toUpperCase();
  const podeCriarGrupo = String(usuario?.nivelSuporte || "").toUpperCase() === "MASTER"
    || ["ADMIN_ORG", "ADMIN_EMPRESA"].includes(tipoUsuario);

  const [conversas, setConversas] = useState<ConversaChat[]>([]);
  const [arquivadas, setArquivadas] = useState<ConversaChat[]>([]);
  const [colegas, setColegas] = useState<ColegaChat[]>([]);
  const [mensagens, setMensagens] = useState<MensagemChat[]>([]);
  const [lidoPorTodosAte, setLidoPorTodosAte] = useState(0);
  const [selecionada, setSelecionada] = useState(0);
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState<FiltroChat>("todas");
  const [aba, setAba] = useState<AbaChat>("conversas");
  const [texto, setTexto] = useState("");
  const [anexoPendente, setAnexoPendente] = useState<AnexoPendenteChat | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [emojiAberto, setEmojiAberto] = useState(false);
  const [grupoAberto, setGrupoAberto] = useState(false);
  const [grupoEditando, setGrupoEditando] = useState<ConversaChat | null>(null);
  const [nomeGrupo, setNomeGrupo] = useState("");
  const [membrosGrupo, setMembrosGrupo] = useState<number[]>([]);
  const [salvandoGrupo, setSalvandoGrupo] = useState(false);
  const [avatarGrupoPreview, setAvatarGrupoPreview] = useState("");
  const [avatarGrupoRemovido, setAvatarGrupoRemovido] = useState(false);
  const [confirmarExcluirGrupo, setConfirmarExcluirGrupo] = useState(false);
  const [excluindoGrupo, setExcluindoGrupo] = useState(false);
  const avatarGrupoInputRef = useRef<HTMLInputElement>(null);
  const [gravando, setGravando] = useState(false);
  const [infoAberto, setInfoAberto] = useState(false);

  const areaMensagensRef = useRef<HTMLDivElement>(null);
  const infoAncoraRef = useRef<HTMLButtonElement>(null);
  const arquivoRef = useRef<HTMLInputElement>(null);
  const gravadorRef = useRef<MediaRecorder | null>(null);
  const pedacosAudioRef = useRef<BlobPart[]>([]);
  const ultimoDigitandoRef = useRef(0);
  const selecionadaRef = useRef(0);
  const ultimasMensagensRef = useRef<Map<number, string>>(new Map());
  const listasInicializadasRef = useRef(false);
  selecionadaRef.current = selecionada;

  /* Um print pendente pertence somente à conversa em que foi colado. */
  useEffect(() => {
    setAnexoPendente(null);
  }, [selecionada]);

  const carregarListas = useCallback(async () => {
    const [ativas, guardadas, equipe] = await Promise.all([
      listarConversasChat(false),
      listarConversasChat(true),
      listarColegasChat(),
    ]);
    const todas = [...ativas, ...guardadas];
    const recebeuNova = listasInicializadasRef.current && todas.some((conversa) => {
      const anterior = ultimasMensagensRef.current.get(conversa.codConversa);
      return conversa.naoLidas > 0
        && !conversa.ultimaMensagemPropria
        && conversa.ultimaMensagemEm !== anterior;
    });
    ultimasMensagensRef.current = new Map(
      todas.map((conversa) => [conversa.codConversa, conversa.ultimaMensagemEm || ""])
    );
    listasInicializadasRef.current = true;
    if (recebeuNova) avisarNotificacao("Nova mensagem no chat interno");
    setConversas(ativas);
    setArquivadas(guardadas);
    setColegas(equipe);
  }, []);

  const carregarMensagens = useCallback(async (codConversa: number) => {
    if (!codConversa) {
      setMensagens([]);
      setLidoPorTodosAte(0);
      return;
    }
    const resposta = await listarMensagensChat(codConversa).catch(() => null);
    if (!resposta?.sucesso || selecionadaRef.current !== codConversa) return;
    setMensagens(resposta.mensagens ?? []);
    setLidoPorTodosAte(Number(resposta.lidoPorTodosAte ?? 0));
    /* Atualiza o badge no clique, sem esperar o próximo ciclo de atualização. */
    setConversas((atuais) => atuais.map((conversa) => (
      conversa.codConversa === codConversa ? { ...conversa, naoLidas: 0 } : conversa
    )));
    setArquivadas((atuais) => atuais.map((conversa) => (
      conversa.codConversa === codConversa ? { ...conversa, naoLidas: 0 } : conversa
    )));
    await marcarConversaLidaChat(codConversa).catch(() => null);
  }, []);

  useEffect(() => {
    if (!aberto) return;
    listasInicializadasRef.current = false;
    setBusca("");
    setEmojiAberto(false);
    void carregarListas();
  }, [aberto, carregarListas]);

  useEffect(() => {
    if (!aberto) return;
    void carregarMensagens(selecionada);
  }, [aberto, selecionada, carregarMensagens]);

  useEffect(() => {
    setInfoAberto(false);
  }, [selecionada]);

  // Polling enquanto o painel esta aberto: listas + mensagens da conversa ativa.
  useEffect(() => {
    if (!aberto) return;
    const intervalo = window.setInterval(() => {
      void carregarListas();
      if (selecionadaRef.current) void carregarMensagens(selecionadaRef.current);
    }, INTERVALO_POLL);
    return () => window.clearInterval(intervalo);
  }, [aberto, carregarListas, carregarMensagens]);

  useEffect(() => {
    if (!aberto) return;
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === "Escape") onFechar();
    };
    document.addEventListener("keydown", aoTeclar);
    return () => document.removeEventListener("keydown", aoTeclar);
  }, [aberto, onFechar]);

  useEffect(() => {
    const area = areaMensagensRef.current;
    if (area) area.scrollTop = area.scrollHeight;
  }, [mensagens]);

  // Fechar o painel (ou desmontar) encerra qualquer gravação em andamento.
  useEffect(() => {
    if (aberto) return;
    gravadorRef.current?.stop();
  }, [aberto]);

  const conversaAtual = useMemo(
    () => [...conversas, ...arquivadas].find((item) => item.codConversa === selecionada) ?? null,
    [conversas, arquivadas, selecionada]
  );

  const statusAutorMensagem = useCallback((mensagem: MensagemChat): StatusColega => {
    const statusAtivo = (status: StatusColega | undefined): StatusColega =>
      status === "AUSENTE" || status === "OCUPADO" ? status : "DISPONIVEL";

    if (mensagem.propria || mensagem.codLogin === usuario?.codLogin) {
      return statusAtivo(usuario?.statusAtendimento);
    }

    const participante = conversaAtual?.participantes.find(
      (item) => item.codLogin === mensagem.codLogin
    );
    if (participante?.online) return statusAtivo(participante.status);

    const colega = colegas.find((item) => item.codLogin === mensagem.codLogin);
    return colega?.online ? statusAtivo(colega.status) : "OFFLINE";
  }, [colegas, conversaAtual, usuario?.codLogin, usuario?.statusAtendimento]);

  const listaVisivel = useMemo(() => {
    /* Aba "Grupos" (chave interna "equipe"): mesma lista/renderização das
       conversas, só que restrita às conversas em grupo. */
    const base = aba === "arquivadas"
      ? arquivadas
      : aba === "equipe"
        ? conversas.filter((item) => item.tipo === "GRUPO")
        : conversas;
    const termo = normalizar(busca.trim());
    return base.filter((item) => {
      if (filtro === "naoLidas" && item.naoLidas === 0) return false;
      if (filtro === "online" && (item.tipo !== "INDIVIDUAL" || item.status === "OFFLINE")) return false;
      if (filtro === "suporte" && !item.suporte) return false;
      if (!termo) return true;
      return normalizar(`${item.nome} ${item.subtitulo} ${item.ultimaMensagem}`).includes(termo);
    });
  }, [aba, arquivadas, conversas, busca, filtro]);

  /** Faixa de avatares do topo: sempre a equipe (colegas), independente da aba. */
  const equipeVisivel = useMemo(() => {
    const termo = normalizar(busca.trim());
    return colegas.filter((colega) => {
      /* O login Master/Suporte só aparece na lista de colegas quando o chip
         "Suporte" está ativo — fora dele, fica reservado para não confundir
         a lista normal da equipe. */
      if (colega.suporte !== (filtro === "suporte")) return false;
      if (filtro === "online" && !colega.online) return false;
      if (
        filtro === "naoLidas"
        && !conversas.some((conversa) =>
          conversa.tipo === "INDIVIDUAL"
          && conversa.codLoginContraparte === colega.codLogin
          && conversa.naoLidas > 0
        )
      ) return false;
      return !termo || normalizar(`${colega.nome} ${colega.cargo}`).includes(termo);
    });
  }, [colegas, conversas, busca, filtro]);

  useEffect(() => {
    if (aba !== "conversas" || !selecionada) return;
    if (!listaVisivel.some((conversa) => conversa.codConversa === selecionada)) {
      setSelecionada(0);
    }
  }, [aba, listaVisivel, selecionada]);

  /** Não lidas por colega, para o badge na faixa de avatares. */
  const naoLidasPorLogin = useMemo(() => {
    const mapa = new Map<number, number>();
    conversas.forEach((conversa) => {
      if (conversa.tipo !== "INDIVIDUAL" || !conversa.codLoginContraparte) return;
      mapa.set(conversa.codLoginContraparte, conversa.naoLidas);
    });
    return mapa;
  }, [conversas]);

  /** Clicar na conversa já aberta fecha o chat; clicar em outra troca normalmente. */
  function alternarSelecao(codConversa: number) {
    setSelecionada((atual) => (atual === codConversa ? 0 : codConversa));
  }

  async function abrirComColega(codLogin: number) {
    const resposta = await abrirConversaDiretaChat(codLogin).catch(() => null);
    if (!resposta?.sucesso || !resposta.codConversa) {
      mostrarToastErro(resposta?.erro || traduzir("Não foi possível abrir a conversa."));
      return;
    }
    setAba("conversas");
    setSelecionada(resposta.codConversa);
    await carregarListas();
  }

  function aoDigitar(valor: string) {
    setTexto(valor);
    if (!selecionada) return;
    const agora = Date.now();
    if (agora - ultimoDigitandoRef.current < INTERVALO_DIGITANDO) return;
    ultimoDigitandoRef.current = agora;
    void sinalizarDigitandoChat(selecionada);
  }

  async function enviar() {
    const corpo = texto.trim();
    if ((!corpo && !anexoPendente) || !selecionada || enviando) return;
    if (anexoPendente) {
      await anexar(anexoPendente.arquivo);
      return;
    }
    setEnviando(true);
    setTexto("");
    try {
      const resposta = await enviarMensagemChat({ codConversa: selecionada, texto: corpo });
      if (resposta?.sucesso === false) {
        setTexto(corpo);
        mostrarToastErro(resposta.erro || traduzir("Não foi possível enviar a mensagem."));
        return;
      }
      await carregarMensagens(selecionada);
      await carregarListas();
    } catch (erro) {
      setTexto(corpo);
      mostrarToastErro(erro instanceof Error ? erro.message : traduzir("Não foi possível enviar a mensagem."));
    } finally {
      setEnviando(false);
    }
  }

  async function anexar(arquivo: File) {
    if (!selecionada) return;
    if (arquivo.size > TAMANHO_MAXIMO_ANEXO) {
      mostrarToastErro(traduzir("O arquivo excede o tamanho máximo permitido."));
      return;
    }
    setEnviando(true);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const leitor = new FileReader();
        leitor.onload = () => resolve(String(leitor.result ?? ""));
        leitor.onerror = () => reject(new Error("Falha ao ler o arquivo."));
        leitor.readAsDataURL(arquivo);
      });
      const resposta = await enviarMensagemChat({
        codConversa: selecionada,
        texto: texto.trim(),
        anexoBase64: base64,
        anexoNome: arquivo.name,
      });
      if (resposta?.sucesso === false) {
        mostrarToastErro(resposta.erro || traduzir("Não foi possível enviar o anexo."));
        return;
      }
      setTexto("");
      setAnexoPendente(null);
      await carregarMensagens(selecionada);
      await carregarListas();
    } catch (erro) {
      mostrarToastErro(erro instanceof Error ? erro.message : traduzir("Não foi possível enviar o anexo."));
    } finally {
      setEnviando(false);
    }
  }

  /** Cola prints no composer sem enviá-los imediatamente, como no WhatsApp. */
  function prepararPrintColado(arquivo: File) {
    if (!selecionada || !arquivo.type.startsWith("image/")) return;
    if (arquivo.size > TAMANHO_MAXIMO_ANEXO) {
      mostrarToastErro(traduzir("O arquivo excede o tamanho máximo permitido."));
      return;
    }
    const conversaId = selecionada;
    const extensao = arquivo.type.split("/")[1]?.replace("jpeg", "jpg") || "png";
    const arquivoNomeado = new File(
      [arquivo],
      arquivo.name && arquivo.name !== "image.png" ? arquivo.name : `print-${Date.now()}.${extensao}`,
      { type: arquivo.type }
    );
    const leitor = new FileReader();
    leitor.onload = () => {
      if (selecionadaRef.current !== conversaId) return;
      setAnexoPendente({ arquivo: arquivoNomeado, previewUrl: String(leitor.result || "") });
    };
    leitor.onerror = () => mostrarToastErro(traduzir("Não foi possível ler o print colado."));
    leitor.readAsDataURL(arquivoNomeado);
  }

  /**
   * Áudio do composer: grava pelo microfone e envia como anexo da conversa —
   * reaproveita o mesmo caminho do clipe de papel, sem endpoint novo.
   */
  async function alternarGravacao() {
    if (gravando) {
      gravadorRef.current?.stop();
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      mostrarToastErro(traduzir("Seu navegador não permite gravar áudio."));
      return;
    }
    try {
      const trilha = await navigator.mediaDevices.getUserMedia({ audio: true });
      const gravador = new MediaRecorder(trilha);
      pedacosAudioRef.current = [];
      gravador.ondataavailable = (evento) => {
        if (evento.data.size) pedacosAudioRef.current.push(evento.data);
      };
      gravador.onstop = () => {
        trilha.getTracks().forEach((faixa) => faixa.stop());
        gravadorRef.current = null;
        setGravando(false);
        const tipo = gravador.mimeType || "audio/webm";
        const blob = new Blob(pedacosAudioRef.current, { type: tipo });
        pedacosAudioRef.current = [];
        if (!blob.size) return;
        const extensao = tipo.includes("ogg") ? "ogg" : tipo.includes("mp4") ? "m4a" : "webm";
        void anexar(new File([blob], `audio-${Date.now()}.${extensao}`, { type: tipo }));
      };
      gravadorRef.current = gravador;
      gravador.start();
      setGravando(true);
    } catch {
      mostrarToastErro(traduzir("Não foi possível acessar o microfone."));
    }
  }

  async function alternarArquivo(conversa: ConversaChat) {
    const resposta = await arquivarConversaChat(conversa.codConversa, !conversa.arquivada).catch(() => null);
    if (resposta?.sucesso === false) {
      mostrarToastErro(resposta.erro || traduzir("Não foi possível arquivar a conversa."));
      return;
    }
    if (selecionada === conversa.codConversa) setSelecionada(0);
    await carregarListas();
  }

  function abrirNovoGrupo() {
    setGrupoEditando(null);
    setNomeGrupo("");
    setMembrosGrupo([]);
    setAvatarGrupoPreview("");
    setAvatarGrupoRemovido(false);
    setGrupoAberto(true);
  }

  function abrirEdicaoGrupo(conversa: ConversaChat) {
    setGrupoEditando(conversa);
    setNomeGrupo(conversa.nome);
    setMembrosGrupo(
      conversa.participantes.filter((p) => p.codLogin !== usuario?.codLogin).map((p) => p.codLogin)
    );
    setAvatarGrupoPreview("");
    setAvatarGrupoRemovido(false);
    setGrupoAberto(true);
  }

  function fecharModalGrupo() {
    setGrupoAberto(false);
    setGrupoEditando(null);
    setNomeGrupo("");
    setMembrosGrupo([]);
    setAvatarGrupoPreview("");
    setAvatarGrupoRemovido(false);
  }

  async function selecionarAvatarGrupo(file: File | null | undefined) {
    if (!file) return;
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(new Error("Falha ao ler o arquivo."));
      reader.readAsDataURL(file);
    }).catch(() => "");
    if (!base64) return;
    setAvatarGrupoPreview(base64);
    setAvatarGrupoRemovido(false);
  }

  async function salvarGrupo() {
    if (!podeCriarGrupo || !nomeGrupo.trim() || !membrosGrupo.length || salvandoGrupo) return;
    setSalvandoGrupo(true);
    try {
      const avatarBase64 = avatarGrupoPreview || undefined;
      const resposta = grupoEditando
        ? await editarGrupoChat({
            codConversa: grupoEditando.codConversa,
            nome: nomeGrupo.trim(),
            participantes: membrosGrupo,
            avatarBase64,
            removerAvatar: avatarGrupoRemovido,
          })
        : await criarGrupoChat({ nome: nomeGrupo.trim(), participantes: membrosGrupo, avatarBase64 });
      const codConversaFinal = resposta?.codConversa || grupoEditando?.codConversa;
      if (!resposta?.sucesso || !codConversaFinal) {
        mostrarToastErro(
          resposta?.erro || traduzir(grupoEditando ? "Não foi possível salvar o grupo." : "Não foi possível criar o grupo.")
        );
        return;
      }
      fecharModalGrupo();
      setAba("conversas");
      setSelecionada(codConversaFinal);
      await carregarListas();
    } finally {
      setSalvandoGrupo(false);
    }
  }

  async function confirmarExclusaoGrupo() {
    if (!grupoEditando || excluindoGrupo) return;
    setExcluindoGrupo(true);
    try {
      const resposta = await excluirGrupoChat(grupoEditando.codConversa);
      if (!resposta?.sucesso) {
        mostrarToastErro(resposta?.erro || traduzir("Não foi possível excluir o grupo."));
        return;
      }
      if (selecionada === grupoEditando.codConversa) setSelecionada(0);
      mostrarToast(traduzir("Grupo excluído."), "sucesso");
      setConfirmarExcluirGrupo(false);
      fecharModalGrupo();
      await carregarListas();
    } finally {
      setExcluindoGrupo(false);
    }
  }

  const mensagensPorDia = useMemo(() => {
    const grupos: Array<{ dia: string; itens: MensagemChat[] }> = [];
    mensagens.forEach((mensagem) => {
      const dia = chaveDia(mensagem.criadaEm);
      const ultimo = grupos[grupos.length - 1];
      if (ultimo && ultimo.dia === dia) ultimo.itens.push(mensagem);
      else grupos.push({ dia, itens: [mensagem] });
    });
    return grupos;
  }, [mensagens]);

  return createPortal(
    <aside className={`${styles.painel} ${aberto ? styles.painelAberto : ""}`} aria-hidden={!aberto}>
        <header className={styles.cabecalho}>
          <span className={styles.cabecalhoIcone} aria-hidden="true">
            <MessageSquare size={22} strokeWidth={1.9} />
          </span>
          <div className={styles.cabecalhoTextos}>
            <div className={styles.cabecalhoTitulo}>{traduzir("Chat interno")}</div>
            <div className={styles.cabecalhoSubtitulo}>
              {traduzir("Converse com sua equipe em tempo real")}
            </div>
          </div>
          <BotaoFechar className={styles.fechar} onClick={onFechar} />
        </header>

        <CampoPesquisa
          className={styles.busca}
          inputClassName={styles.buscaInput}
          type="text"
          placeholder={aba === "equipe" ? "Pesquisar grupos" : "Pesquisar conversas"}
          value={busca}
          onChange={(evento) => setBusca(evento.target.value)}
          iconeInicio={<Search size={18} strokeWidth={1.9} aria-hidden="true" />}
        />

        <div className={styles.filtros}>
          <div className={styles.filtrosChips} role="group" aria-label={traduzir("Filtrar conversas")}>
            {FILTROS.map((opcao) => (
              <ChipSelecao
                key={opcao.chave}
                selecionado={filtro === opcao.chave}
                className={styles.filtroChip}
                iconeInicio={<opcao.Icone size={12} />}
                onClick={() => {
                  setFiltro(opcao.chave);
                  /* "Suporte" filtra as conversas com o time de suporte — a lista
                     de colegas suporte continua acessível pela faixa de avatares
                     do topo, já que a aba "Grupos" não é mais a lista de colegas. */
                  if (opcao.chave === "suporte" && aba === "equipe") setAba("conversas");
                }}
              >
                {opcao.rotulo}
              </ChipSelecao>
            ))}
          </div>
          {podeCriarGrupo && (
            <button
              type="button"
              className={`${styles.filtroAcao} tooltip tooltipUp`}
              data-tooltip={traduzir("Novo grupo")}
              aria-label={traduzir("Novo grupo")}
              disabled={!colegas.length}
              onClick={abrirNovoGrupo}
            >
              <UserRoundPlus size={17} strokeWidth={1.9} />
            </button>
          )}
        </div>

        {aba !== "arquivadas" && equipeVisivel.length > 0 && (
          <div className={styles.faixaColegas}>
            <button
              type="button"
              className={`${styles.colegaAtalho} ${styles.colegaTodos} ${
                selecionada === 0 ? styles.colegaAtalhoAtivo : ""
              }`}
              onClick={() => setSelecionada(0)}
            >
              <span className={styles.colegaAtalhoIcone} aria-hidden="true">
                <Users size={20} strokeWidth={1.9} />
              </span>
              <span className={styles.colegaAtalhoNome}>{traduzir("Todos")}</span>
            </button>
            {equipeVisivel.map((colega) => {
              const naoLidas = naoLidasPorLogin.get(colega.codLogin) ?? 0;
              return (
                <button
                  key={colega.codLogin}
                  type="button"
                  className={styles.colegaAtalho}
                  onClick={() => void abrirComColega(colega.codLogin)}
                >
                  <span className={styles.colegaAtalhoAvatar}>
                    <Avatar nome={colega.nome} avatarId={colega.avatarId} status={colega.status} tamanho={34} />
                    {naoLidas > 0 && <span className={styles.colegaBadge}>{naoLidas}</span>}
                  </span>
                  <span className={styles.colegaAtalhoNome} data-i18n-db="true">
                    {colega.nome.split(" ")[0]}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <div className={styles.corpo}>
          {!conversaAtual && (
          <div className={styles.lista}>
            {listaVisivel.length === 0 ? (
              <p className={styles.vazio}>
                {traduzir(
                  aba === "arquivadas"
                    ? "Nenhuma conversa arquivada."
                    : aba === "equipe"
                      ? "Nenhum grupo encontrado."
                      : "Nenhuma conversa por aqui."
                )}
              </p>
            ) : (
              listaVisivel.map((conversa) => (
                <div
                  key={conversa.codConversa}
                  className={`${styles.itemConversa} ${
                    selecionada === conversa.codConversa ? styles.itemConversaAtiva : ""
                  }`}
                >
                  <button
                    type="button"
                    className={styles.itemBotao}
                    onClick={() => alternarSelecao(conversa.codConversa)}
                  >
                    <Avatar
                      nome={conversa.nome}
                      avatarId={conversa.avatarId}
                      status={conversa.tipo === "INDIVIDUAL" ? conversa.status : ""}
                      tamanho={36}
                      grupo={conversa.tipo === "GRUPO"}
                    />
                    <span className={styles.itemTextos}>
                      <span className={styles.itemNome} data-i18n-db="true">{conversa.nome}</span>
                      <span className={styles.itemPreview}>
                        {conversa.digitando ? (
                          <em className={styles.digitando}>{traduzir("digitando...")}</em>
                        ) : (
                          <>
                            {conversa.ultimaMensagemPropria && conversa.ultimaMensagem && (
                              conversa.ultimaMensagemLida ? (
                                <CheckCheck size={13} strokeWidth={2.2} className={styles.itemCheckLido} />
                              ) : (
                                <Check size={13} strokeWidth={2.2} className={styles.itemCheck} />
                              )
                            )}
                            <span data-i18n-db="true">
                              {conversa.ultimaMensagem || traduzir("Sem mensagens ainda")}
                            </span>
                          </>
                        )}
                      </span>
                    </span>
                    <span className={styles.itemLado}>
                      <span className={styles.itemHora}>{horaCurta(conversa.ultimaMensagemEm)}</span>
                      {conversa.naoLidas > 0 && (
                        <span className={styles.itemBadge} aria-label={`${traduzir("Mensagens não lidas")}: ${conversa.naoLidas}`}>
                          {totalNoBadge(conversa.naoLidas)}
                        </span>
                      )}
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`${styles.itemArquivar} tooltip tooltipUp`}
                    data-tooltip={traduzir(conversa.arquivada ? "Desarquivar" : "Arquivar")}
                    aria-label={traduzir(conversa.arquivada ? "Desarquivar" : "Arquivar")}
                    onClick={() => void alternarArquivo(conversa)}
                  >
                    {conversa.arquivada ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                  </button>
                </div>
              ))
            )}
          </div>
          )}

          {conversaAtual && (
            <section className={styles.conversa}>
              <header className={styles.conversaCabecalho}>
                <Avatar
                  nome={conversaAtual.nome}
                  avatarId={conversaAtual.avatarId}
                  status={conversaAtual.tipo === "INDIVIDUAL" ? conversaAtual.status : ""}
                  tamanho={34}
                  grupo={conversaAtual.tipo === "GRUPO"}
                />
                <div className={styles.conversaTextos}>
                  <strong data-i18n-db="true">{conversaAtual.nome}</strong>
                  <span>
                    {conversaAtual.digitando
                      ? traduzir("digitando...")
                      : conversaAtual.tipo === "GRUPO"
                        ? `${conversaAtual.participantes.length} ${traduzir("membros")}`
                        : conversaAtual.status === "DISPONIVEL"
                          ? traduzir("Online agora")
                          : traduzir("Offline")}
                  </span>
                </div>
                <div className={styles.conversaAcoes}>
                  {/* Voz e vídeo entram no layout, mas ainda sem chamada ligada. */}
                  <button
                    type="button"
                    className={`${styles.conversaAcao} tooltip tooltipUp`}
                    data-tooltip={traduzir("Chamada de voz (em breve)")}
                    aria-label={traduzir("Chamada de voz")}
                    disabled
                  >
                    <Phone size={16} strokeWidth={1.9} />
                  </button>
                  <button
                    type="button"
                    className={`${styles.conversaAcao} tooltip tooltipUp`}
                    data-tooltip={traduzir("Chamada de vídeo (em breve)")}
                    aria-label={traduzir("Chamada de vídeo")}
                    disabled
                  >
                    <Video size={16} strokeWidth={1.9} />
                  </button>
                  <button
                    ref={infoAncoraRef}
                    type="button"
                    className={`${styles.conversaAcao} ${
                      conversaAtual.tipo === "GRUPO" ? "" : "tooltip tooltipUp"
                    }`}
                    data-tooltip={
                      conversaAtual.tipo === "GRUPO" ? undefined : conversaAtual.subtitulo || conversaAtual.nome
                    }
                    aria-label={traduzir("Detalhes da conversa")}
                    aria-haspopup={conversaAtual.tipo === "GRUPO" ? "true" : undefined}
                    aria-expanded={conversaAtual.tipo === "GRUPO" && !podeCriarGrupo ? infoAberto : undefined}
                    onClick={() => {
                      if (conversaAtual.tipo !== "GRUPO") return;
                      if (podeCriarGrupo) abrirEdicaoGrupo(conversaAtual);
                      else setInfoAberto((atual) => !atual);
                    }}
                  >
                    <Info size={16} strokeWidth={1.9} />
                  </button>
                  {conversaAtual.tipo === "GRUPO" && !podeCriarGrupo && (
                    <Popover
                      ancora={infoAncoraRef}
                      aberto={infoAberto}
                      onFechar={() => setInfoAberto(false)}
                      largura={230}
                      alinhamento="fim"
                    >
                      <div className={styles.infoMembros}>
                        <strong className={styles.infoMembrosTitulo}>
                          {traduzir("Membros do grupo")}
                        </strong>
                        {conversaAtual.participantes.map((participante) => (
                          <div key={participante.codLogin} className={styles.infoMembroItem}>
                            <Avatar
                              nome={participante.nome}
                              avatarId={participante.avatarId}
                              status={participante.status}
                              tamanho={30}
                            />
                            <span className={styles.infoMembroTextos}>
                              <span data-i18n-db="true">{participante.nome}</span>
                              {participante.cargo && (
                                <span className={styles.infoMembroCargo} data-i18n-db="true">
                                  {participante.cargo}
                                </span>
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    </Popover>
                  )}
                  <button
                    type="button"
                    className={`${styles.conversaAcao} tooltip tooltipUp`}
                    data-tooltip={traduzir("Fechar conversa")}
                    aria-label={traduzir("Fechar conversa")}
                    onClick={() => setSelecionada(0)}
                  >
                    <X size={16} strokeWidth={1.9} />
                  </button>
                </div>
              </header>

              <div className={styles.mensagens} ref={areaMensagensRef}>
                {mensagens.length === 0 ? (
                  <p className={styles.vazio}>{traduzir("Comece a conversa enviando uma mensagem.")}</p>
                ) : (
                  mensagensPorDia.map((grupo) => (
                    <div key={grupo.dia} className={styles.grupoDia}>
                      <div className={styles.separadorDia}>
                        <span>{rotuloDia(grupo.itens[0]?.criadaEm, traduzir)}</span>
                      </div>
                      {grupo.itens.map((mensagem) => (
                        <div
                          key={mensagem.codMensagem}
                          className={`${styles.linhaMensagem} ${
                            mensagem.propria ? styles.linhaPropria : styles.linhaOutro
                          }`}
                        >
                          {!mensagem.propria && (
                            <Avatar
                              nome={mensagem.autorNome}
                              avatarId={mensagem.autorAvatarId}
                              status={statusAutorMensagem(mensagem)}
                              tamanho={28}
                            />
                          )}
                          <div className={styles.balao}>
                            <span className={styles.balaoAutor} data-i18n-db="true">
                              {mensagem.autorNome}
                            </span>
                            {mensagem.texto && (
                              <span className={styles.balaoTexto} data-i18n-db="true">
                                {mensagem.texto}
                              </span>
                            )}
                            {mensagem.anexoId && (
                              <AnexoMensagem id={mensagem.anexoId} nome={mensagem.anexoNome || traduzir("Arquivo")} />
                            )}
                            <span className={styles.balaoRodape}>
                              {horaCurta(mensagem.criadaEm)}
                              {mensagem.propria &&
                                (mensagem.codMensagem <= lidoPorTodosAte ? (
                                  <CheckCheck size={13} strokeWidth={2.2} className={styles.checkLido} />
                                ) : (
                                  <Check size={13} strokeWidth={2.2} />
                                ))}
                            </span>
                          </div>
                          {mensagem.propria && (
                            <Avatar
                              nome={mensagem.autorNome}
                              avatarId={mensagem.autorAvatarId}
                              status={statusAutorMensagem(mensagem)}
                              tamanho={28}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </div>

              <div className={styles.composer}>
                <input
                  ref={arquivoRef}
                  type="file"
                  hidden
                  onChange={(evento) => {
                    const arquivo = evento.target.files?.[0];
                    evento.target.value = "";
                    if (arquivo) void anexar(arquivo);
                  }}
                />
                {anexoPendente ? (
                  <div className={styles.anexoPendente}>
                    <img src={anexoPendente.previewUrl} alt="" />
                    <span>
                      <strong>{traduzir("Print pronto para enviar")}</strong>
                      <small>{anexoPendente.arquivo.name}</small>
                    </span>
                    <button
                      type="button"
                      className={`${styles.anexoPendenteRemover} tooltip tooltipUp`}
                      data-tooltip={traduzir("Remover print")}
                      aria-label={traduzir("Remover print")}
                      disabled={enviando}
                      onClick={() => setAnexoPendente(null)}
                    >
                      <X aria-hidden="true" />
                    </button>
                  </div>
                ) : null}
                <button
                  type="button"
                  className={`${styles.composerAcao} tooltip tooltipUp`}
                  data-tooltip={traduzir("Anexar arquivo")}
                  aria-label={traduzir("Anexar arquivo")}
                  disabled={enviando}
                  onClick={() => arquivoRef.current?.click()}
                >
                  <Paperclip size={17} strokeWidth={1.9} />
                </button>

                <div className={styles.composerCampo}>
                  <input
                    className={styles.composerInput}
                    type="text"
                    maxLength={4000}
                    autoComplete={AUTOCOMPLETE_BLOQUEADO}
                    placeholder={traduzir("Escreva uma mensagem...")}
                    value={texto}
                    onChange={(evento) => aoDigitar(evento.target.value)}
                    onPaste={(evento) => {
                      const itemImagem = Array.from(evento.clipboardData.items).find(
                        (item) => item.kind === "file" && item.type.startsWith("image/")
                      );
                      const arquivo = itemImagem?.getAsFile();
                      if (!arquivo) return;
                      evento.preventDefault();
                      prepararPrintColado(arquivo);
                    }}
                    onKeyDown={(evento) => {
                      if (evento.key === "Enter" && !evento.shiftKey) {
                        evento.preventDefault();
                        void enviar();
                      }
                    }}
                  />
                  <div className={styles.composerEmoji}>
                    <button
                      type="button"
                      className={`${styles.composerEmojiBotao} tooltip tooltipUp`}
                      data-tooltip={traduzir("Emojis")}
                      aria-label={traduzir("Emojis")}
                      aria-expanded={emojiAberto}
                      onClick={() => setEmojiAberto((atual) => !atual)}
                    >
                      <Smile size={17} strokeWidth={1.9} />
                    </button>
                    {emojiAberto && (
                      <div className={styles.emojiPainel}>
                        {EMOJIS.map((emoji) => (
                          <button
                            key={emoji}
                            type="button"
                            className={styles.emojiBotao}
                            onClick={() => {
                              setTexto((atual) => `${atual}${emoji}`);
                              setEmojiAberto(false);
                            }}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  className={`${styles.composerAcao} ${styles.composerAcaoRedonda} ${
                    gravando ? styles.composerGravando : ""
                  } tooltip tooltipUp`}
                  data-tooltip={traduzir(gravando ? "Parar e enviar áudio" : "Gravar áudio")}
                  aria-label={traduzir(gravando ? "Parar e enviar áudio" : "Gravar áudio")}
                  aria-pressed={gravando}
                  disabled={enviando && !gravando}
                  onClick={() => void alternarGravacao()}
                >
                  <Mic size={17} strokeWidth={1.9} />
                </button>

                <button
                  type="button"
                  className={styles.composerEnviar}
                  aria-label={traduzir("Enviar mensagem")}
                  disabled={(!texto.trim() && !anexoPendente) || enviando}
                  onClick={() => void enviar()}
                >
                  <Send size={17} strokeWidth={2} />
                </button>
              </div>
            </section>
          )}
        </div>

        <footer className={styles.rodape}>
          {ABAS.map((item) => (
            <button
              key={item.chave}
              type="button"
              className={`${styles.rodapeAba} ${aba === item.chave ? styles.rodapeAbaAtiva : ""}`}
              aria-pressed={aba === item.chave}
              onClick={() => {
                setAba(item.chave);
                setSelecionada(0);
              }}
            >
              <item.Icone size={16} />
              {traduzir(item.rotulo)}
              {item.chave === "arquivadas" && arquivadas.length > 0 && (
                <span className={styles.rodapeAbaBadge}>{arquivadas.length}</span>
              )}
            </button>
          ))}
        </footer>

        {podeCriarGrupo && grupoAberto && (
          <div className={styles.grupoOverlay}>
            <div className={styles.grupoCaixa}>
              <div className={styles.grupoTopo}>
                <strong>{traduzir(grupoEditando ? "Editar grupo" : "Novo grupo")}</strong>
                <button
                  type="button"
                  className={styles.grupoFechar}
                  aria-label={traduzir("Fechar")}
                  onClick={fecharModalGrupo}
                >
                  <X size={16} strokeWidth={2} />
                </button>
              </div>
              <div className={styles.grupoAvatarCampo}>
                <button
                  type="button"
                  className={styles.grupoAvatarBotao}
                  aria-label={traduzir("Trocar foto do grupo")}
                  onClick={() => avatarGrupoInputRef.current?.click()}
                >
                  {avatarGrupoPreview ? (
                    <img src={avatarGrupoPreview} alt="" />
                  ) : (
                    <Avatar
                      nome={nomeGrupo || "Grupo"}
                      avatarId={avatarGrupoRemovido ? "" : grupoEditando?.avatarId}
                      grupo
                      tamanho={64}
                    />
                  )}
                  <span className={styles.grupoAvatarOverlay}>
                    <Camera size={16} strokeWidth={2} />
                  </span>
                </button>
                <input
                  ref={avatarGrupoInputRef}
                  type="file"
                  accept="image/*"
                  className={styles.inputOculto}
                  onChange={(evento) => {
                    void selecionarAvatarGrupo(evento.target.files?.[0]);
                    evento.target.value = "";
                  }}
                />
                {(avatarGrupoPreview || (grupoEditando?.avatarId && !avatarGrupoRemovido)) && (
                  <button
                    type="button"
                    className={styles.grupoAvatarRemover}
                    onClick={() => {
                      setAvatarGrupoPreview("");
                      setAvatarGrupoRemovido(true);
                    }}
                  >
                    {traduzir("Remover foto")}
                  </button>
                )}
              </div>
              <CampoTexto
                rotulo="Nome do grupo"
                value={nomeGrupo}
                placeholder="Ex.: Financeiro"
                onChange={(evento) => setNomeGrupo(evento.target.value)}
              />
              <span className={styles.grupoRotulo}>{traduzir("Participantes")}</span>
              <div className={styles.grupoLista}>
                {colegas.map((colega) => (
                  <label key={colega.codLogin} className={styles.grupoItem}>
                    <Checkbox
                      checado={membrosGrupo.includes(colega.codLogin)}
                      onChange={(marcado) =>
                        setMembrosGrupo((atual) =>
                          marcado
                            ? [...atual, colega.codLogin]
                            : atual.filter((id) => id !== colega.codLogin)
                        )
                      }
                    />
                    <Avatar nome={colega.nome} avatarId={colega.avatarId} tamanho={28} />
                    <span data-i18n-db="true">{colega.nome}</span>
                  </label>
                ))}
              </div>
              <button
                type="button"
                className={styles.grupoSalvar}
                disabled={!nomeGrupo.trim() || !membrosGrupo.length || salvandoGrupo}
                onClick={() => void salvarGrupo()}
              >
                {traduzir(grupoEditando ? "Salvar alterações" : "Criar grupo")}
              </button>
              {grupoEditando && (
                <button
                  type="button"
                  className={styles.grupoExcluir}
                  disabled={salvandoGrupo || excluindoGrupo}
                  onClick={() => setConfirmarExcluirGrupo(true)}
                >
                  <Trash2 size={15} strokeWidth={2} />
                  {traduzir("Excluir grupo")}
                </button>
              )}
            </div>
          </div>
        )}

        <ModalConfirmacao
          aberto={confirmarExcluirGrupo}
          titulo="Excluir grupo"
          mensagem="Esta ação remove o grupo e o histórico de mensagens para todos os participantes. Deseja continuar?"
          textoConfirmar="Excluir grupo"
          perigo
          processando={excluindoGrupo}
          onConfirmar={() => void confirmarExclusaoGrupo()}
          onCancelar={() => setConfirmarExcluirGrupo(false)}
        />
    </aside>,
    document.body
  );
}
